import React from "react";
import { useTranslation } from "react-i18next";
import "./InvestDetail.scss";
import {
  numberToMoney,
  formatAmountMoney,
  truncateDecimals
} from "../../lib/formatter";
import ReactTooltip from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { startInvestFlow, canIInvest } from "../../redux/epics/invest";
import User from "../../lib/models/user.model";
import InvestmentModel from "../../lib/models/investment.model";
import ModalActions from "../../redux/reducers/modal.reducer";
import campaignCompleteIconBlue from "assets/images/campaignComplete-icon-blue.svg";
import alertIcon from "assets/images/Alert.svg";
import infoTooltipIcon from "assets/images/info-tooltip2.svg";
import { useIsPhone } from "../../hooks/useIsPhone";

enum ICampaingTypes {
  Debt = 1,
  Shares = 2,
  ConvertibleNote = 3
}

enum ICampaingState {
  Open = 1,
  Awarded = 2,
  Close = 6
}

const CampaignState = {
  active: 1,
  awarded: 2,
  deserted: 3,
  cancelled: 4,
  inactive: 5
};

export type IProps = {
  investDetail: any;
};

const InvestDetail = ({ investDetail }: IProps) => {
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  const getCampaignPercentage = (
    investmentAmount: number,
    requestedAmount: number,
    limit: boolean
  ) => {
    const percentage: number = Number(
      (investmentAmount * 100) / requestedAmount
    );
    if (limit) {
      return percentage <= 100 ? percentage : "100";
    }
    return truncateDecimals(percentage, 2);
  };

  const appStore: any = useSelector((store) => store);
  const userState = useSelector(() => {
    return { ...appStore.user };
  });
  const dispatch = useDispatch();
  let userPayload = {};
  const documentTypeArray: any = User.documentType;
  if (userState.userInfo) {
    const { user } = userState.userInfo;
    userPayload = {
      personType: user.person_type,
      documentNumber: user.document_number,
      documentType: documentTypeArray[user.document_type],
      name: user.name,
      lastName: user.last_name,
      userId: user.id
    };
  }

  const campaignInactive =
    investDetail.campaign_state !== CampaignState.active ||
    investDetail.investment_state !== InvestmentModel.status.active ||
    investDetail.investment_amount >= investDetail.requested_amount;

  const handleIncrease = () => {
    if (!campaignInactive) {
      const action = !userState.userInfo
        ? startInvestFlow(investDetail.campaign_id)
        : canIInvest(investDetail.campaign_id, userPayload);
      dispatch(action);
    }
  };

  const handleCancel = () => {
    if (!campaignInactive) {
      dispatch(
        ModalActions.setConditionalModalState(true, "CancellationModal", {
          campaignId: investDetail.campaign_id,
          amount: investDetail.net_amount
        })
      );
    }
  };

  // Bloque "Recaudo campaña" para la tarjeta abierta expandida en mobile.
  // Se apila verticalmente: Estado + Meta, (Garantía solo Deuda), Monto
  // recaudado + barra de progreso. `accentColor` es el color de la línea de
  // negocio y `showGuarantee` habilita el bloque de garantía (solo Deuda).
  const renderMobileRecaudo = (
    accentColor: string,
    showGuarantee: boolean,
    titlePadding: number = 12,
    contentWrapPadding: number = 0
  ) => {
    const stateLabel =
      investDetail?.campaign_state == ICampaingState.Open
        ? t("investDetail.stateOpen")
        : investDetail?.campaign_state == ICampaingState.Awarded
        ? t("investDetail.stateAwarded")
        : t("investDetail.stateClose");
    const meta = investDetail?.requested_amount
      ? `${formatAmountMoney(investDetail.requested_amount)}M`
      : "0M";
    const raised = investDetail?.investment_amount
      ? `${formatAmountMoney(investDetail.investment_amount)}M`
      : "0M";
    const pct = getCampaignPercentage(
      investDetail?.investment_amount || 0,
      investDetail?.requested_amount,
      false
    );
    const pctBar = getCampaignPercentage(
      investDetail?.investment_amount || 0,
      investDetail?.requested_amount,
      true
    );
    const gPct = Number(investDetail?.guarantee_percentage);
    const gLabel = gPct > 0 ? `FNG ${gPct}%` : "No aplica";

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ padding: `0 ${titlePadding}px` }}>
          <h3 style={{ color: "#475061", fontSize: "18px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
            Recaudo campaña
          </h3>
        </div>

        {/* Estado + Meta */}
        <div style={{ padding: `0 ${contentWrapPadding}px` }}>
        <div style={{ opacity: "1", borderLeft: `1px solid ${accentColor}`, padding: "0 16px", display: "flex", alignItems: "flex-start", gap: "16px" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Estado</span>
            <span style={{ background: "#0BBEF0", color: "white", padding: "4px 16px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", width: "fit-content" }}>
              {stateLabel}
            </span>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Meta</span>
            <span style={{ whiteSpace: "nowrap" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>COP </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>{meta}</span>
            </span>
          </div>
        </div>
        </div>

        {/* Garantía campaña (solo Deuda) */}
        {showGuarantee && (
          <div style={{ padding: `0 ${contentWrapPadding}px` }}>
          <div style={{ opacity: "1", borderLeft: `1px solid ${accentColor}`, padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Garantía campaña</span>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{gLabel}</span>
          </div>
          </div>
        )}

        {/* Monto recaudado */}
        <div style={{ padding: `0 ${contentWrapPadding}px` }}>
        <div style={{ opacity: "1", borderLeft: `1px solid ${accentColor}`, padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Monto recaudado:</span>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>COP </span>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>{raised}</span>
          </div>
          <div style={{ height: "19px", width: "100%", background: "#DFDFDF", borderRadius: "10px", overflow: "hidden" }}>
            <div style={{ background: accentColor, height: "100%", width: `${pctBar}%`, borderRadius: "10px" }}></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ color: accentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{pct}%</span>
            <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>recaudado</span>
          </div>
        </div>
        </div>
      </div>
    );
  };

  if (investDetail?.business_line === ICampaingTypes.ConvertibleNote) {
    const isOpenCampaign =
      investDetail?.campaign_state == ICampaingState.Open;
    const campaignStateLabel =
      investDetail?.campaign_state == ICampaingState.Open
        ? t("investDetail.stateOpen")
        : investDetail?.campaign_state == ICampaingState.Awarded
        ? t("investDetail.stateAwarded")
        : t("investDetail.stateClose");

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: isPhone ? 0 : "16px", boxSizing: "border-box", paddingBottom: "12px" }}>
        {/* Tu inversión */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "16px", paddingBottom: isPhone ? 0 : "16px" }}>
          <div style={{ padding: isPhone ? "12px 24px 0" : "0 24px" }}>
            <h3 style={{ color: "#475061", fontSize: isPhone ? "18px" : "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              {t("investDetail.title")}
            </h3>
          </div>

          <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", padding: isPhone ? "0 12px" : "0 24px", gap: isPhone ? "12px" : "16px" }}>
            {/* Capital */}
            <div style={{ opacity: "1", flex: 1, padding: "8px 16px", borderLeft: "1px solid #496CFF", borderTopRightRadius: isPhone ? "8px" : 0, borderBottomRightRadius: isPhone ? "8px" : 0, display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Capital</span>
              <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: "#496CFF", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{t("investDetail.currency")} </span>
                <span style={{ color: "#496CFF", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
                  {investDetail?.net_amount >= 0 ? numberToMoney(investDetail.net_amount, "$", ".", ",", ".", false, true) : 0}
                </span>
              </span>
            </div>

            {/* Pendiente por convertir */}
            <div style={{ opacity: "1", flex: 1, padding: "8px 16px", borderLeft: "1px solid #496CFF", borderTopRightRadius: isPhone ? "8px" : 0, borderBottomRightRadius: isPhone ? "8px" : 0, display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: isPhone ? "24px" : "16px", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
                Pendiente por convertir
                <img className="investDetail__icon" src={infoTooltipIcon} alt="info" data-tip={t("financedYourself.tooltipCapitalManagement")} data-for="convertible-note-tooltip" style={{ width: isPhone ? 20 : 16, height: isPhone ? 20 : 16, flexShrink: 0, position: "relative", top: isPhone ? 0 : "-5px", cursor: "pointer" }} />
              </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px", whiteSpace: "nowrap" }}>
                {t("investDetail.currency")} {investDetail?.net_amount >= 0 ? numberToMoney(investDetail.net_amount, "$", ".", ",", ".", false, true) : 0}
              </span>
            </div>
          </div>
          <ReactTooltip id="convertible-note-tooltip" className="tooltip_invest_detail" place="top" effect="solid" />
        </div>

        {isOpenCampaign && (
          <>
        {/* Recaudo campaña */}
        {isPhone ? <div style={{ marginTop: "9px" }}>{renderMobileRecaudo("#496CFF", false, 28, 16)}</div> : (
        <div style={{ padding: "0 24px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ opacity: "1", width: "100%", padding: "12px 16px", borderLeft: "1px solid #496CFF", display: "flex", flexDirection: isPhone ? "column" : "row", justifyContent: "space-between", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "16px" : "0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Monto recaudado:</span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>COP </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                {investDetail?.investment_amount ? `${formatAmountMoney(investDetail.investment_amount)}M` : "0M"}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "12px" : "24px", width: isPhone ? "100%" : "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#496CFF", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                  {getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, false)}%
                </span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>recaudado</span>
              </div>
              <div style={{ width: isPhone ? "100%" : "304px", height: "13px", background: "#DFDFDF", borderRadius: "6.5px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, true)}%`, background: "#496CFF", borderRadius: "8px" }}></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Meta: COP </span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                  {investDetail?.requested_amount ? `${formatAmountMoney(investDetail.requested_amount)}M` : "0M"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Estado</span>
              <span style={{ background: "#0BBEF0", color: "white", padding: "4px 16px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", width: "fit-content" }}>
                {campaignStateLabel}
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: isPhone ? "12px" : 0, padding: isPhone ? "12px 16px" : 0 }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: isPhone ? "8px" : "16px", width: isPhone ? "100%" : "auto" }}>
            <button
              style={{ background: "#496CFF", color: "white", padding: "8px 24px", borderRadius: "48px", border: "none", fontFamily: "Nunito", fontSize: "16px", fontWeight: 700, lineHeight: "24px", width: "148px", flexShrink: 0, whiteSpace: "nowrap", textAlign: "center", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1 }}
              onClick={handleIncrease}
            >
              {t("investment.investmentsList.increase")}
            </button>
            <button
              style={{ background: "transparent", color: "#475061", border: "none", fontFamily: "Nunito", fontSize: "14px", fontWeight: 400, lineHeight: "20px", flexShrink: 0, whiteSpace: "nowrap", textAlign: "center", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1, position: "relative", left: "-6px" }}
              onClick={handleCancel}
            >
              {t("investment.investmentsList.cancelInvestment")}
            </button>
          </div>
        </div>
          </>
        )}
      </div>
    );
  }

  if (investDetail?.business_line === ICampaingTypes.Shares) {
    const isOpenCampaign =
      investDetail?.campaign_state == ICampaingState.Open;
    const sharesPrice = Number(investDetail?.shares_price) || 0;
    const sharesForPackage = Number(investDetail?.shares_for_package) || 0;
    const totalShares = sharesPrice
      ? Math.round(investDetail.net_amount / sharesPrice)
      : 0;
    const acquiredPackages =
      sharesPrice && sharesForPackage
        ? Math.round(investDetail.net_amount / sharesPrice / sharesForPackage)
        : 0;
    const campaignStateLabel =
      investDetail?.campaign_state == ICampaingState.Open
        ? t("investDetail.stateOpen")
        : investDetail?.campaign_state == ICampaingState.Awarded
        ? t("investDetail.stateAwarded")
        : t("investDetail.stateClose");

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "16px", boxSizing: "border-box", paddingBottom: "12px" }}>
        {/* Tu inversión */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "16px", paddingBottom: "16px" }}>
          <div style={{ padding: "0 24px" }}>
            <h3 style={{ color: "#475061", fontSize: isPhone ? "18px" : "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              {t("investDetail.title")}
            </h3>
          </div>

          <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", padding: isPhone ? "0 12px" : "0 24px", gap: isPhone ? "12px" : "16px" }}>
            {/* Capital */}
            <div style={{ opacity: "1", flex: 1, padding: "8px 16px", borderLeft: "1px solid #7D7BFA", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Capital</span>
              <span>
                <span style={{ color: "#7D7BFA", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{t("investDetail.currency")} </span>
                <span style={{ color: "#7D7BFA", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
                  {investDetail?.net_amount >= 0 ? numberToMoney(investDetail.net_amount, "$", ".", ",", ".", false, true) : 0}
                </span>
              </span>
            </div>

            {/* Paquetes de acciones adquiridos */}
            <div style={{ opacity: "1", flex: 1, padding: "8px 16px", borderLeft: "1px solid #7D7BFA", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>
                {t("investment.investmentsList.packagesAmount")}
              </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{acquiredPackages}</span>
            </div>

            {/* Total de acciones adquiridas */}
            <div style={{ opacity: "1", flex: 1, padding: "8px 16px", borderLeft: "1px solid #7D7BFA", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>
                {t("investment.investmentsList.sharesAmount")}
              </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{totalShares}</span>
            </div>
          </div>
        </div>

        {isOpenCampaign && (
          <>
        {/* Recaudo campaña */}
        {isPhone ? renderMobileRecaudo("#7D7BFA", false, 28, 16) : (
        <div style={{ padding: "0 24px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ opacity: "1", width: "100%", padding: "12px 0 12px 16px", borderLeft: "1px solid #7D7BFA", display: "flex", flexDirection: isPhone ? "column" : "row", justifyContent: "space-between", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "16px" : "0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Monto recaudado:</span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>COP </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                {investDetail?.investment_amount ? `${formatAmountMoney(investDetail.investment_amount)}M` : "0M"}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "12px" : "24px", width: isPhone ? "100%" : "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#7D7BFA", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                  {getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, false)}%
                </span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>recaudado</span>
              </div>
              <div style={{ width: isPhone ? "100%" : "304px", height: "13px", background: "#DFDFDF", borderRadius: "6.5px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, true)}%`, background: "#7D7BFA", borderRadius: "8px" }}></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Meta: COP </span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>
                  {investDetail?.requested_amount ? `${formatAmountMoney(investDetail.requested_amount)}M` : "0M"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Estado</span>
              <span style={{ background: "#0BBEF0", color: "white", padding: "4px 16px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", width: "fit-content" }}>
                {campaignStateLabel}
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "16px 24px 0", position: "relative", left: "4.5px" }}>
          <button
            style={{ background: "#7D7BFA", color: "white", padding: "8px 24px", borderRadius: "48px", border: "none", fontFamily: "Nunito", fontSize: "16px", fontWeight: 700, lineHeight: "24px", width: "148px", flexShrink: 0, whiteSpace: "nowrap", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1 }}
            onClick={handleIncrease}
          >
            {t("investment.investmentsList.increase")}
          </button>
          <button
            style={{ background: "transparent", color: "#475061", border: "none", fontFamily: "Nunito", fontSize: "14px", fontWeight: 400, lineHeight: "20px", flexShrink: 0, whiteSpace: "nowrap", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1, position: "relative", left: "-6px" }}
            onClick={handleCancel}
          >
            {t("investment.investmentsList.cancelInvestment")}
          </button>
        </div>
          </>
        )}
      </div>
    );
  }

  // "Inversiones activas": la inversión está activa y la campaña ya fue
  // adjudicada/cerrada (campaign_state !== Abierta). En este caso, en vez de
  // "Recaudo campaña" se muestra el "Detalle de pagos".
  const isActiveInvestment =
    investDetail?.investment_state === InvestmentModel.status.active &&
    investDetail?.campaign_state != ICampaingState.Open;

  // Estado del pago (pyme_payment_campaign.state_id): 1 = Al día, 2 = Pendiente.
  // Cualquier otro valor se trata como "Al día" por defecto.
  const isPendingPayment = Number(investDetail?.payment_state_id) === 2;

  // "Inversiones finalizadas": la campaña tiene sus pagos finalizados
  // (pyme_payment_campaign.state_id === 5). En este caso, el "Detalle de pagos"
  // se muestra en su variante "Finalizado".
  const isFinishedInvestment = Number(investDetail?.payment_state_id) === 5;

  const paidQuotes = Number(investDetail?.paidQuotes) || 0;
  const totalQuotes = Number(investDetail?.totalQuotes) || 0;
  const overdueQuotes = Number(investDetail?.overdueQuotes) || 0;
  const nextQuota =
    investDetail?.next_quota != null
      ? investDetail.next_quota
      : paidQuotes + 1;
  const quotesProgress =
    totalQuotes > 0 ? Math.min((paidQuotes / totalQuotes) * 100, 100) : 0;

  const guaranteePercentage = Number(investDetail?.guarantee_percentage);
  const guaranteeLabel =
    guaranteePercentage > 0 ? `FNG ${guaranteePercentage}%` : "No aplica";

  // "Inversiones en mora": la campaña adjudicada entró en mora. El estado del
  // pago (pyme_payment_campaign.state_id) define la variante que se pinta:
  // 3 = En mora, 4 = Garantía ejecutada, 6 = Garantía en ejecución.
  const paymentStateId = Number(investDetail?.payment_state_id);
  const isEnMora = paymentStateId === 3;
  const isGuaranteeExecuted = paymentStateId === 4;
  const isGuaranteeInExecution = paymentStateId === 6;
  const isMoraInvestment =
    isEnMora || isGuaranteeExecuted || isGuaranteeInExecution;
  const moraStateLabel =
    isEnMora
      ? "En mora"
      : isGuaranteeExecuted
      ? "Garantía ejecutada"
      : isGuaranteeInExecution
      ? "Garantía en ejecución"
      : investDetail?.payment_state_name || "Garantía en ejecución";
  const moraBadgeStyle = isEnMora
    ? { background: "#BB2569", color: "#FFFFFF" }
    : isGuaranteeExecuted
    ? { background: "#DFDFDF", color: "#475061" }
    : { background: "#FFE0C2", color: "#475061" };
  const moraAccentColor = isEnMora ? "#BB2569" : "#475061";
  // "Garantía ejecutada" (4) y "Garantía en ejecución" (6) comparten el mismo
  // layout agrupado del "Detalle de pagos" (columnas de ancho fijo, badge que
  // se ajusta al texto, fecha por segmentos). "En mora" (3) mantiene su diseño.
  const isGuaranteeState = isGuaranteeInExecution || isGuaranteeExecuted;
  // En mobile, cualquier estado de "Detalle de pagos" (En mora, Garantía
  // ejecutada, Garantía en ejecución) agrupa sus 4 datos (Estado del pago,
  // Fecha inicio de mora / de ejecución, Garantía campaña, Días en mora) en
  // una grilla de 2 columnas en vez del layout de ancho fijo pensado para
  // desktop, que en pantallas angostas cortaba texto y empujaba "Días en
  // mora" fuera del contenedor.
  const useMobileGroupedMora = isPhone;
  // Estilo de celda para la grilla mobile: sin anchos fijos, se acomoda al
  // 50% que le da la columna de la grilla.
  const moraGridCellStyle: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" };
  // Ancho fijo de la primera columna en ambos bloques de la grilla mobile
  // (Estado del pago / Garantía campaña). Con "flex: 1" en las dos columnas,
  // cada bloque se reparte 50/50 en base a SU PROPIO contenido: el badge de
  // "Estado del pago" (whiteSpace: nowrap) es más ancho que el texto de
  // "Garantía campaña", así que ese bloque termina con una primera columna
  // más ancha que la del segundo, desalineando "Fecha de ejecución" y "Días
  // en mora" aunque compartan el mismo estilo. Un ancho fijo e idéntico para
  // ambos bloques garantiza que la segunda columna arranque siempre en el
  // mismo punto.
  const moraGridFirstColumnWidth =
    isPhone && isGuaranteeExecuted ? "149px" : "151px";
  const paymentStateRowStyle: React.CSSProperties = isGuaranteeState
    ? { display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "0", width: "100%", borderRadius: "8px" }
    : { display: "flex", flexWrap: isPhone ? "wrap" : "nowrap", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" };
  const paymentStateColumnStyle: React.CSSProperties = isGuaranteeState
    ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px", width: "182px", flexShrink: 0 }
    : { display: "flex", flexDirection: "column", gap: "12px", width: "min-content" };
  const paymentStateDateColumnStyle: React.CSSProperties = isGuaranteeState
    ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px", width: "176px", flexShrink: 0 }
    : { display: "flex", flexDirection: "column", gap: "12px" };
  const paymentStateDaysColumnStyle: React.CSSProperties = isGuaranteeState
    ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px", flex: "1 1 0" }
    : { display: "flex", flexDirection: "column", gap: "12px" };
  const paymentStateContainerStyle: React.CSSProperties = isGuaranteeState
    ? { opacity: "1", width: "100%", height: "100%", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "12px" }
    : { opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" };
  const paymentStateBadgeStyle: React.CSSProperties = isGuaranteeState
    ? { display: "inline-flex", justifyContent: "center", alignItems: "center", background: moraBadgeStyle.background, color: moraBadgeStyle.color, padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", whiteSpace: "nowrap", width: "151px", boxSizing: "border-box", cursor: "default", fontFamily: "Nunito" }
    : { display: "inline-flex", justifyContent: "center", alignItems: "center", background: moraBadgeStyle.background, color: moraBadgeStyle.color, padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", width: "100%", boxSizing: "border-box", cursor: "default", overflow: "hidden", fontFamily: "Nunito" };
  const moraBadgeStyleResolved: React.CSSProperties = useMobileGroupedMora
    ? { ...paymentStateBadgeStyle, width: "100%" }
    : paymentStateBadgeStyle;
  const moraDateLabel = isGuaranteeExecuted
    ? "Fecha de ejecución"
    : "Fecha inicio de mora";
  const moraStartDate =
    investDetail?.moraStartDate || investDetail?.overdueStartDate;
  const daysOverdue = Number(investDetail?.daysOverdue) || 0;
  const moraTooltip = isGuaranteeExecuted
    ? "La empresa superó el límite de días en mora y se ejecutó la garantía. El capital cubierto fue acreditado en tu ‘Disponible’."
    : isGuaranteeInExecution
    ? "La empresa no pagó las cuotas en mora dentro del plazo estipulado. Se procederá a activar la ejecución de la garantía."
    : 'El emisor no ha pagado sus cuotas a tiempo. Para más detalles sobre la mora, descarga el "Reporte consolidado de inversiones"';
  // El denominador de la barra debe cubrir al menos "pagadas + en mora" para
  // que cada segmento conserve su proporción real incluso cuando esos
  // conteos superen totalQuotes (datos desalineados entre el calendario de
  // pagos y el conteo de cuotas reportado por backend).
  const moraQuotesTotal = Math.max(totalQuotes, paidQuotes + overdueQuotes);
  const moraPaidProgress =
    moraQuotesTotal > 0
      ? Math.min((paidQuotes / moraQuotesTotal) * 100, 100)
      : 0;
  const moraOverdueProgress =
    moraQuotesTotal > 0
      ? Math.min(((paidQuotes + overdueQuotes) / moraQuotesTotal) * 100, 100)
      : 0;

  const formatPaymentDate = (value: any): string => {
    if (!value) {
      return "--/--/----";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "--/--/----";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
  };

  // En mobile la tarjeta de deuda se apila verticalmente para todos los
  // estados (una sola columna, y los datos de "Tu inversión" en una sola
  // columna) para que el despliegue tenga sentido en pantallas angostas.

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "16px", boxSizing: "border-box", padding: isPhone ? "0 0 12px" : "16px 16px 12px" }}>
      <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", width: "100%", gap: isPhone ? "12px" : "16px" }}>

      {/* LEFT COLUMN: Tu inversión */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: isPhone ? "8px" : "16px" }}>
        <div style={{ padding: isPhone ? "12px 24px 0" : "0 12px" }}>
          <h3 style={{ color: "#475061", fontSize: isPhone ? "18px" : "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
            {t("investDetail.title")}
          </h3>
        </div>

        <div style={{ flex: 1, display: "grid", gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr", gridTemplateRows: isPhone ? "auto" : "1fr 1fr", rowGap: isPhone ? "12px" : "16px", columnGap: "16px", padding: isPhone ? "0 12px" : undefined }}>
          {/* Capital */}
          <div style={{ opacity: "1", display: "flex", flexDirection: "column", gap: isPhone ? "8px" : "12px", borderLeft: "1px solid #04A3AE", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", padding: "8px 16px"}}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Capital</span>
            <span style={{ color: "#04A3AE", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
              {t("investDetail.currency")} {investDetail?.net_amount >= 0 ? numberToMoney(investDetail.net_amount, "$", ".", ",", ".", false, true) : 0}
            </span>
          </div>

          {/* Valor pagado */}
          <div style={{ opacity: "1", display: "flex", flexDirection: "column", gap: isPhone ? "8px" : "12px", borderLeft: "1px solid #04A3AE", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", padding: "8px 16px" }}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: isPhone ? "24px" : "16px", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
              {t("investDetail.subTitle1") || "Valor pagado en capital"}
              <img className="investDetail__icon" src={infoTooltipIcon} alt="info" data-tip={t("investDetail.tooltipDebOne")} data-for="invest-detail-tooltip" style={{ width: isPhone ? 20 : 16, height: isPhone ? 20 : 16, flexShrink: 0, position: "relative", top: isPhone ? 0 : "-5px", cursor: "pointer" }} />
            </span>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
              {t("investDetail.currency")} {investDetail?.value_in_paid_capital >= 0 ? numberToMoney(investDetail.value_in_paid_capital, "$", ".", ",", ".", false, true) : 0}
            </span>
          </div>

          {/* Capital pendiente */}
          <div style={{ opacity: "1", display: "flex", flexDirection: "column", gap: isPhone ? "8px" : "12px", borderLeft: "1px solid #04A3AE", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", padding: "8px 16px" }}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: isPhone ? "24px" : "16px", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
              {t("investDetail.subTitle2") || "Capital pendiente por pagar"}
              <img className="investDetail__icon" src={infoTooltipIcon} alt="info" data-tip={t("investDetail.tooltipDebTwo")} data-for="invest-detail-tooltip" style={{ width: isPhone ? 20 : 16, height: isPhone ? 20 : 16, flexShrink: 0, position: "relative", top: isPhone ? 0 : "-5px", cursor: "pointer" }} />
            </span>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
              {t("investDetail.currency")} {numberToMoney(Math.max(0, Number(investDetail?.capital_pending_payable) || 0), "$", ".", ",", ".", false, true)}
            </span>
          </div>

          {/* Rendimientos pagados */}
          <div style={{ opacity: "1", display: "flex", flexDirection: "column", gap: isPhone ? "8px" : "12px", borderLeft: "1px solid #04A3AE", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", padding: "8px 16px" }}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: isPhone ? "24px" : "16px", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
              {t("investDetail.subTitle3") || "Rendimientos pagados"}
              <img className="investDetail__icon" src={infoTooltipIcon} alt="info" data-tip={t("investDetail.tooltipDebThree")} data-for="invest-detail-tooltip" style={{ width: isPhone ? 20 : 16, height: isPhone ? 20 : 16, flexShrink: 0, position: "relative", top: isPhone ? 0 : "-5px", cursor: "pointer" }} />
            </span>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
              {t("investDetail.currency")} {investDetail?.returns_paid >= 0 ? numberToMoney(investDetail.returns_paid, "$", ".", ",", ".", false, true) : 0}
            </span>
          </div>
        </div>
        <ReactTooltip id="invest-detail-tooltip" className="tooltip_invest_detail" place="top" effect="solid" />
      </div>

      {isMoraInvestment ? (
        /* RIGHT COLUMN: Detalle de pagos (En mora / Garantía) */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px", padding: isPhone ? "0 16px" : undefined, boxSizing: "border-box" }}>
          <div style={{ padding: "0 12px" }}>
            <h3 style={{ color: "#475061", fontSize: isPhone ? "18px" : "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              {isPhone ? "Recaudo campaña" : "Detalle de pagos"}
            </h3>
          </div>

          {useMobileGroupedMora ? (
            isEnMora ? (
              /* Mobile: Figma "estado mora" — mismo patrón de columnas fijas
                 (139px/133px, badge 118px) que "Detalle de pagos" en
                 inversiones activas, con acento en rosa (#BB2569). */
              <>
                <div style={{ opacity: "1", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    {/* Estado del pago */}
                    <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Estado del pago</span>
                      <span data-tip={moraTooltip} data-for="invest-detail-tooltip" style={{ width: "118px", background: "#BB2569", color: "#FFFFFF", padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", boxSizing: "border-box", overflow: "hidden", cursor: "default" }}>
                        {moraStateLabel}
                      </span>
                    </div>
                    {/* Fecha límite de pago */}
                    <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Fecha límite de pago</span>
                      <div style={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "flex-start" }}>
                        {formatPaymentDate(moraStartDate).split("/").map((part, index, parts) => (
                          <React.Fragment key={`${part}-${index}`}>
                            <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{part}</span>
                            {index < parts.length - 1 ? <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>/</span> : null}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ opacity: "1", height: "56px", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    {/* Garantía campaña */}
                    <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Garantía campaña</span>
                      <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
                    </div>
                    {/* Días en mora */}
                    <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Días en mora</span>
                      <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px", textAlign: "left" }}>{daysOverdue}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : isGuaranteeExecuted ? (
              /* Mobile: Figma "Garantía ejecutada" — mismo patrón de
                 columnas fijas (139px/133px) que "estado mora", con badge
                 gris de 127px y texto/fecha en gris neutro (#475061). */
              <>
                <div style={{ opacity: "1", height: "72px", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    {/* Estado del pago */}
                    <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Estado del pago</span>
                      <span data-tip={moraTooltip} data-for="invest-detail-tooltip" style={{ width: "127px", background: "#DFDFDF", color: "#475061", padding: "4px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: 700, lineHeight: "16px", textAlign: "center", whiteSpace: "nowrap", boxSizing: "border-box", overflow: "hidden", cursor: "default" }}>
                        {moraStateLabel}
                      </span>
                    </div>
                    {/* Fecha de ejecución */}
                    <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{moraDateLabel}</span>
                      <div style={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "flex-start" }}>
                        {formatPaymentDate(moraStartDate).split("/").map((part, index, parts) => (
                          <React.Fragment key={`${part}-${index}`}>
                            <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{part}</span>
                            {index < parts.length - 1 ? <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>/</span> : null}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ opacity: "1", height: "56px", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    {/* Garantía campaña */}
                    <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Garantía campaña</span>
                      <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
                    </div>
                    {/* Días en mora */}
                    <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Días en mora</span>
                      <div style={{ display: "flex", justifyContent: "flex-start" }}>
                        <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{isGuaranteeExecuted || (isGuaranteeInExecution && daysOverdue >= 180) ? "+180" : daysOverdue}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Mobile: Figma "Recaudo campaña" (Garantía en ejecución) —
                 Estado+Fecha y Garantía+Días van en dos bloques con borde
                 separados (no una única grilla). */
              <>
                <div style={paymentStateContainerStyle}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", width: "100%" }}>
                    {/* Estado del pago */}
                    <div style={{ width: moraGridFirstColumnWidth, flexShrink: 0, ...moraGridCellStyle }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>
                        Estado del pago
                      </span>
                      <span data-tip={moraTooltip} data-for="invest-detail-tooltip" style={moraBadgeStyleResolved}>
                        {moraStateLabel}
                      </span>
                    </div>
                    {/* Fecha inicio de mora */}
                    <div style={{ flex: 1, minWidth: 0, ...moraGridCellStyle }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{moraDateLabel}</span>
                      {isGuaranteeState ? (
                        <div style={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                          {formatPaymentDate(moraStartDate).split("/").map((part, index, parts) => (
                            <React.Fragment key={`${part}-${index}`}>
                              <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{part}</span>
                              {index < parts.length - 1 ? <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>/</span> : null}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{formatPaymentDate(moraStartDate)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={paymentStateContainerStyle}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", width: "100%" }}>
                    {/* Garantía campaña */}
                    <div style={{ width: moraGridFirstColumnWidth, flexShrink: 0, ...moraGridCellStyle }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Garantía campaña</span>
                      <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
                    </div>
                    {/* Días en mora */}
                    <div style={{ flex: 1, minWidth: 0, ...moraGridCellStyle }}>
                      <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px" }}>Días en mora</span>
                      <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{isGuaranteeExecuted || (isGuaranteeInExecution && daysOverdue >= 180) ? "+180" : daysOverdue}</span>
                    </div>
                  </div>
                </div>
              </>
            )
          ) : (
            <div style={paymentStateContainerStyle}>
              <div style={paymentStateRowStyle}>
                {/* Estado del pago */}
                <div style={paymentStateColumnStyle}>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px", whiteSpace: "nowrap" }}>
                    Estado del pago
                  </span>
                  <span data-tip={moraTooltip} data-for="invest-detail-tooltip" style={moraBadgeStyleResolved}>
                    {moraStateLabel}
                  </span>
                </div>
                {/* Fecha inicio de mora / Fecha de ejecución */}
                <div style={paymentStateDateColumnStyle}>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{moraDateLabel}</span>
                  {isGuaranteeState ? (
                    <div style={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                      {formatPaymentDate(moraStartDate).split("/").map((part, index, parts) => (
                        <React.Fragment key={`${part}-${index}`}>
                          <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{part}</span>
                          {index < parts.length - 1 ? <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>/</span> : null}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{formatPaymentDate(moraStartDate)}</span>
                  )}
                </div>
                {/* Días en mora */}
                <div style={paymentStateDaysColumnStyle}>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Días en mora</span>
                  <span style={{ color: moraAccentColor, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{isGuaranteeExecuted || (isGuaranteeInExecution && daysOverdue >= 180) ? "+180" : daysOverdue}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignSelf: "stretch", alignItems: "center", justifyContent: "flex-start", gap: "4px", width: "100%", paddingRight: isGuaranteeInExecution ? "14px" : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: isGuaranteeInExecution ? "1 1 0" : "initial" }}>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px", width: isGuaranteeState ? "134px" : undefined }}>Garantía campaña:</span>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px", width: isGuaranteeState ? "117px" : undefined }}>{guaranteeLabel}</span>
                </div>
                {isGuaranteeInExecution ? <div style={{ width: "173px", height: "24px" }} /> : null}
              </div>
            </div>
          )}

          <div style={{  opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: isPhone ? "14px" : "16px", fontWeight: 400, lineHeight: isPhone ? "20px" : "24px" }}>Nº de cuotas</span>
            <div style={{ position: "relative", height: "19px", width: "100%", background: moraOverdueProgress >= 100 ? "#BB2569" : "#DFDFDF", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, background: "#BB2569", height: "100%", width: `${moraOverdueProgress}%`, borderRadius: "10px" }}></div>
              <div style={{ position: "absolute", top: 0, left: 0, background: "#04A3AE", height: "100%", width: `${moraPaidProgress}%`, borderRadius: "10px" }}></div>
            </div>
            <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", alignItems: isPhone ? "flex-start" : "center", gap: "12px", flexWrap: "nowrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#04A3AE", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{paidQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{paidQuotes === 1 ? "Cuota pagada" : "Cuotas pagadas"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#BB2569", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{overdueQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{overdueQuotes === 1 ? "Cuota en mora" : "Cuotas en mora"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#DFDFDF", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{totalQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Cuotas totales</span>
              </div>
            </div>
          </div>
        </div>
      ) : isFinishedInvestment ? (
        /* RIGHT COLUMN: Detalle de pagos (Finalizado) */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px", padding: isPhone ? "0 16px" : undefined, boxSizing: "border-box" }}>
          <div style={{ padding: "0 12px" }}>
            <h3 style={{ color: "#475061", fontSize: "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              Detalle de pagos
            </h3>
          </div>

          <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", height: isPhone ? "72px" : undefined, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", flexWrap: "nowrap", justifyContent: isPhone ? "flex-start" : "space-between", alignItems: "center", gap: "16px" }}>
              {/* Estado del pago */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: isPhone ? "139px" : "160px", flexShrink: 0 }}>
                <span style={{ color: "#475061", fontSize: isPhone ? "14px" : "16px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Estado del pago</span>
                <span data-tip="La campaña finalizó con éxito todos sus pagos." data-for="invest-detail-tooltip" style={{ background: "#2C5697", color: "white", padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", width: "118px", boxSizing: "border-box", cursor: "default" }}>
                  Finalizado
                </span>
              </div>
              {/* Icono campaña finalizada */}
              <div style={{ flex: "1 1 0", display: "flex", flexDirection: isPhone ? "column" : "row", justifyContent: "flex-start", alignItems: isPhone ? "center" : "flex-start" }}>
                <img src={campaignCompleteIconBlue} alt="Campaña finalizada" style={{ width: "50px", height: "50px", objectFit: "contain", flexShrink: 0 }} />
              </div>
              {/* Mensaje (solo desktop: en mobile se agrupa abajo con "Conoce más campañas") */}
              {!isPhone && (
                <div style={{ width: "245px", flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "12px" }}>
                  <span style={{ alignSelf: "stretch", textAlign: "left", color: "#475061", fontSize: "18px", fontWeight: 800, lineHeight: "24px", wordWrap: "break-word" }}>
                    ¡ Los pagos de la empresa han finalizado !
                  </span>
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: "0 16px", borderLeft: isPhone ? "1px solid #04A3AE" : "none", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
            {isPhone ? (
              <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", gap: "8px" }}>
                <div style={{ width: "257px", color: "#475061", fontSize: "18px", fontFamily: "Nunito", fontWeight: 800, lineHeight: "20px", wordWrap: "break-word" }}>
                  ¡ Los pagos de la empresa han finalizado !
                </div>
                <div style={{ width: "257px" }}>
                  <div style={{ color: "#475061", fontSize: "12px", fontFamily: "Nunito", fontWeight: 400, lineHeight: "14px", wordWrap: "break-word" }}>
                    Conoce más campañas disponibles
                  </div>
                  <div>
                    <span style={{ color: "#475061", fontSize: "12px", fontFamily: "Nunito", fontWeight: 400, lineHeight: "14px", wordWrap: "break-word" }}>para invertir,</span>
                    <span style={{ color: "#475061", fontSize: "15px", fontFamily: "Nunito", fontWeight: 400, lineHeight: "14px", wordWrap: "break-word" }}> </span>
                    <span
                      onClick={() => dispatch(push("/"))}
                      style={{ color: "#0BBEF0", fontSize: "15px", fontFamily: "Nunito", fontWeight: 700, textDecoration: "underline", lineHeight: "14px", wordWrap: "break-word", cursor: "pointer" }}
                    >
                      ir a vitrina
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Conoce más campañas disponibles para invertir, </span>
                <span
                  onClick={() => dispatch(push("/"))}
                  style={{ color: "#0BBEF0", fontSize: "16px", fontWeight: 700, textDecoration: "underline", lineHeight: "24px", cursor: "pointer" }}
                >
                  ir a vitrina
                </span>
              </div>
            )}
          </div>

          <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Nº de cuotas</span>
            <div style={{ height: "19px", width: "100%", background: quotesProgress >= 100 ? "#04A3AE" : "#DFDFDF", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ background: "#04A3AE", height: "100%", width: `${quotesProgress}%`, borderRadius: "10px" }}></div>
            </div>
            <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "6px" : "24px", flexWrap: isPhone ? "nowrap" : "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#04A3AE", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{paidQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{paidQuotes === 1 ? "Cuota pagada" : "Cuotas pagadas"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#BB2569", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{overdueQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{overdueQuotes === 1 ? "Cuota en mora" : "Cuotas en mora"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#DFDFDF", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{totalQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Cuotas totales</span>
              </div>
            </div>
          </div>
        </div>
      ) : isActiveInvestment ? (
        /* RIGHT COLUMN: Detalle de pagos */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px", padding: isPhone ? "0 16px" : undefined, boxSizing: "border-box" }}>
          <div style={{ padding: "0 12px" }}>
            <h3 style={{ color: "#475061", fontSize: "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              Detalle de pagos
            </h3>
          </div>

          {isPhone ? (
            /* Mobile: Estado+Fecha y Garantía+Próxima cuota agrupados en dos
               bloques de 2 columnas (layout intacto desde antes del ajuste
               de desktop de más abajo). */
            <>
              <div style={{ opacity: "1", height: "72px", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                  {/* Estado del pago */}
                  <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Estado del pago</span>
                    <span data-tip={isPendingPayment ? "La cuota actual está pendiente de pago, pero aún quedan días antes de alcanzar la fecha límite de pago." : "La campaña esta al día con el pago de su última cuota."} data-for="invest-detail-tooltip" style={{ width: "118px", background: isPendingPayment ? "#FDDA25" : "#0DFC5F", color: isPendingPayment ? "#475061" : "#1D252D", padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", boxSizing: "border-box", overflow: "hidden", cursor: "default" }}>
                      {isPendingPayment ? "Pendiente" : "Al día"}
                    </span>
                  </div>
                  {/* Fecha de próximo pago / Fecha límite de pago */}
                  <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{isPendingPayment ? "Fecha límite de pago" : "Fecha próximo pago"}</span>
                    <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{formatPaymentDate(investDetail?.next_payment_date)}</span>
                  </div>
                </div>
              </div>

              <div style={{ opacity: "1", height: "56px", boxSizing: "border-box", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ alignSelf: "stretch", borderRadius: "8px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                  {/* Garantía campaña */}
                  <div style={{ width: "139px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Garantía campaña</span>
                    <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
                  </div>
                  {/* Próxima cuota / Cuota actual */}
                  <div style={{ width: "133px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{isPendingPayment ? "Cuota actual" : "Próxima cuota"}</span>
                    <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>{nextQuota}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Desktop: Estado, Fecha de próximo pago y Próxima cuota en una
               sola fila; Garantía campaña en su propia línea (ver captura
               de referencia). */
            <>
              <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "32px" }}>
                  {/* Estado del pago */}
                  <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>Estado del pago</span>
                    <span data-tip={isPendingPayment ? "La cuota actual está pendiente de pago, pero aún quedan días antes de alcanzar la fecha límite de pago." : "La campaña esta al día con el pago de su última cuota."} data-for="invest-detail-tooltip" style={{ width: "118px", background: isPendingPayment ? "#FDDA25" : "#0DFC5F", color: isPendingPayment ? "#475061" : "#1D252D", padding: "4px 12px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", textAlign: "center", boxSizing: "border-box", overflow: "hidden", cursor: "default" }}>
                      {isPendingPayment ? "Pendiente" : "Al día"}
                    </span>
                  </div>
                  {/* Fecha de próximo pago / Fecha límite de pago */}
                  <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{isPendingPayment ? "Fecha límite de pago" : "Fecha de próximo pago"}</span>
                    <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{formatPaymentDate(investDetail?.next_payment_date)}</span>
                  </div>
                  {/* Próxima cuota / Cuota actual */}
                  <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ color: "#475061", fontSize: "14px", fontWeight: 400, lineHeight: "20px", whiteSpace: "nowrap" }}>{isPendingPayment ? "Cuota actual" : "Próxima cuota"}</span>
                    <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{nextQuota}</span>
                  </div>
                </div>
              </div>

              <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Garantía campaña:</span>
                  <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
                </div>
              </div>
            </>
          )}

          <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Nº de cuotas</span>
            <div style={{ height: "19px", width: "100%", background: quotesProgress >= 100 ? "#04A3AE" : "#DFDFDF", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ background: "#04A3AE", height: "100%", width: `${quotesProgress}%`, borderRadius: "10px" }}></div>
            </div>
            <div style={{ display: "flex", flexDirection: isPhone ? "column" : "row", alignItems: isPhone ? "flex-start" : "center", gap: isPhone ? "12px" : "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#04A3AE", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{paidQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>{paidQuotes === 1 ? "Cuota pagada" : "Cuotas pagadas"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: "#DFDFDF", borderRadius: "50%" }}></span>
                <span style={{ color: "#1D252D", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{totalQuotes}</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Cuotas totales</span>
              </div>
            </div>
          </div>
        </div>
      ) : isPhone ? (
        renderMobileRecaudo("#04A3AE", true, 28, 16)
      ) : (
        /* RIGHT COLUMN: Recaudo campaña */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px", padding: isPhone ? "0 16px" : undefined, boxSizing: "border-box" }}>
          <div style={{ padding: "0 12px" }}>
            <h3 style={{ color: "#475061", fontSize: "20px", fontWeight: 700, lineHeight: "24px", margin: 0, fontFamily: "Nunito" }}>
              Recaudo campaña
            </h3>
          </div>

          <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", alignItems: "flex-start" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Estado</span>
              <span style={{ background: "#0BBEF0", color: "white", padding: "4px 16px", borderRadius: "4px", fontSize: "14px", fontWeight: 700, lineHeight: "20px", width: "fit-content" }}>
                {investDetail?.campaign_state == ICampaingState.Open
                      ? t("investDetail.stateOpen")
                      : investDetail?.campaign_state == ICampaingState.Awarded
                      ? t("investDetail.stateAwarded")
                      : t("investDetail.stateClose")}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Garantía campaña:</span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{guaranteeLabel}</span>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Meta</span>
              <span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>COP </span>
                <span style={{ color: "#475061", fontSize: "16px", fontWeight: 800, lineHeight: "24px" }}>{investDetail?.requested_amount ? `${formatAmountMoney(investDetail.requested_amount)}M` : "400M"}</span>
              </span>
            </div>
          </div>

          <div style={{ opacity: "1", borderLeft: "1px solid #04A3AE", padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Monto recaudado:</span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>COP </span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{investDetail?.investment_amount ? `${formatAmountMoney(investDetail.investment_amount)}M` : "360M"}</span>
            </div>
            <div style={{ height: "19px", width: "100%", background: "#DFDFDF", borderRadius: "10px", overflow: "hidden" }}>
               <div style={{ background: "#04A3AE", height: "100%", width: `${getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, true)}%`, borderRadius: "10px" }}></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#04A3AE", fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>{getCampaignPercentage(investDetail?.investment_amount || 0, investDetail?.requested_amount, false)}%</span>
              <span style={{ color: "#475061", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>recaudado</span>
            </div>
          </div>

        </div>
      )}
      </div>

      {/* marginTop compensa el gap:16 del contenedor padre para que el espacio antes del disclaimer quede en 20px (16 - 4 + 8 de padding propio), igual que en el Figma.
          El espacio final después del texto debe ser 8px (dato confirmado): ese 8px ya lo aporta el padding propio del recuadro blanco (padding: "8px 9px" más abajo), así que aquí paddingBottom queda en 0 y marginBottom anula el paddingBottom:12 de este contenedor para no sumar espacio extra. */}
      {isGuaranteeExecuted && (
        <div style={{ alignSelf: "stretch", paddingTop: "8px", paddingBottom: 0, borderRadius: "8px", display: "flex", flexDirection: "column", gap: "4px", marginTop: "-4px", marginBottom: "-12px" }}>
          <div style={{ flex: "1 1 0", padding: "8px 9px", background: "white", borderRadius: "4px", display: "flex", alignItems: "center", gap: "10px" }}>
            <img src={alertIcon} alt="Alerta" style={{ width: 29, height: 29, flexShrink: 0 }} />
            <div style={{ flex: "1 1 0", display: "flex", flexDirection: "column" }}>
              <span style={{ color: "#475061", fontSize: "14px", fontWeight: 700, lineHeight: "20px", fontFamily: "Nunito" }}>Estimado usuario:</span>
              <span style={{ color: "#475061", fontSize: isPhone ? "12px" : "14px", fontWeight: 400, lineHeight: isPhone ? "16px" : "20px", fontFamily: "Nunito", textAlign: "justify" }}>
                Recuerda que puedes solicitar tu Certificado de Derechos Patrimoniales comunicándote al correo{" "}
                <a href="mailto:a2censo@bvc.com.co" style={{ color: "#475061", fontSize: isPhone ? "12px" : "14px", fontWeight: 400, textDecoration: "underline", fontFamily: "Nunito" }}>a2censo@bvc.com.co</a>
                {" "}o por nuestros canales de atención y si lo deseas, iniciar un proceso de cobranza directamente con el emisor.
              </span>
            </div>
          </div>
        </div>
      )}

      {!isActiveInvestment && !isFinishedInvestment && !isMoraInvestment && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "16px 24px 0", position: "relative", left: "4.5px" }}>
          <button
             style={{ background: "#04A3AE", color: "white", padding: "8px 24px", borderRadius: "48px", border: "none", fontFamily: "Nunito", fontSize: "16px", fontWeight: 700, lineHeight: "24px", width: "148px", flexShrink: 0, whiteSpace: "nowrap", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1 }}
             onClick={() => {
              if (!campaignInactive) {
                const action = !userState.userInfo
                  ? startInvestFlow(investDetail.campaign_id)
                  : canIInvest(investDetail.campaign_id, userPayload);
                dispatch(action);
              }
            }}
          >
            Aumentar
          </button>
          
          <button
             style={{ background: "transparent", color: "#475061", border: "none", fontFamily: "Nunito", fontSize: "14px", fontWeight: 400, lineHeight: "20px", flexShrink: 0, whiteSpace: "nowrap", cursor: campaignInactive ? "not-allowed" : "pointer", opacity: campaignInactive ? 0.5 : 1, position: "relative", left: "-6px" }}
             onClick={() => {
              if (!campaignInactive) {
                dispatch(
                  ModalActions.setConditionalModalState(
                    true,
                    "CancellationModal",
                    {
                      campaignId: investDetail.campaign_id,
                      amount: investDetail.net_amount
                    }
                  )
                );
              }
            }}
          >
            {t("investDetail.cancelButton") || "Cancelar inversión"}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvestDetail;
